/**
 * `RegulatoryClass` — CONTRACTS §2.6, **contagem canonica: 16**.
 *
 * O enum e contrato do produto; o subconjunto vendavel e perfil de instancia (CONTRACTS §1.3).
 * `OUTRO` e o default **fail-closed**: participa de zero promocoes e nao aparece em vitrine
 * ate ser reclassificado. Nao ha `@default` na coluna do banco (REG-010).
 */

export const REGULATORY_CLASSES = [
  'MIP',
  'RX_SIMPLES',
  'RX_RETENCAO',
  'CTRL_A',
  'CTRL_B',
  'CTRL_C1',
  'CTRL_C2',
  'CTRL_C3',
  'CTRL_C4',
  'CTRL_C5',
  'CORRELATO',
  'COSMETICO',
  'ALIMENTO',
  'NBCAL',
  'SANEANTE',
  'OUTRO',
] as const;

export type RegulatoryClass = (typeof REGULATORY_CLASSES)[number];

/** CONTRACTS §2.6 — a classe para a qual o motor cai quando nao sabe. Nega tudo. */
export const FAIL_CLOSED_REGULATORY_CLASS: RegulatoryClass = 'OUTRO';

/** Medicamentos sob controle especial (Portaria SVS/MS 344/1998). Rejeitados **sempre**. */
export const CONTROLLED_CLASSES = [
  'CTRL_A',
  'CTRL_B',
  'CTRL_C1',
  'CTRL_C2',
  'CTRL_C3',
  'CTRL_C4',
  'CTRL_C5',
] as const satisfies readonly RegulatoryClass[];

/** Tarja vermelha — receita simples e receita com retencao. */
export const PRESCRIPTION_CLASSES = [
  'RX_SIMPLES',
  'RX_RETENCAO',
] as const satisfies readonly RegulatoryClass[];

/** Toda classe que e medicamento. `NBCAL` **nao** e medicamento (doc 02 §3.1). */
export const MEDICATION_CLASSES = [
  'MIP',
  ...PRESCRIPTION_CLASSES,
  ...CONTROLLED_CLASSES,
] as const satisfies readonly RegulatoryClass[];

/** CONTRACTS §10.2b — default de `regulatory.sellableClasses` no perfil de instancia. */
export const DEFAULT_SELLABLE_CLASSES = [
  'MIP',
  'CORRELATO',
  'COSMETICO',
  'ALIMENTO',
  'NBCAL',
  'SANEANTE',
] as const satisfies readonly RegulatoryClass[];

/**
 * CONTRACTS §2.6 — classes que o **schema do perfil rejeita no boot**: nao ha gate,
 * nao ha flag, nao ha combinacao que as torne visiveis.
 */
export const PROFILE_FORBIDDEN_CLASSES = [
  ...CONTROLLED_CLASSES,
  'OUTRO',
] as const satisfies readonly RegulatoryClass[];

const CONJUNTO_CLASSES: ReadonlySet<string> = new Set<string>(REGULATORY_CLASSES);
const CONJUNTO_CONTROLADOS: ReadonlySet<string> = new Set<string>(CONTROLLED_CLASSES);
const CONJUNTO_MEDICAMENTOS: ReadonlySet<string> = new Set<string>(MEDICATION_CLASSES);
const CONJUNTO_TARJA_VERMELHA: ReadonlySet<string> = new Set<string>(PRESCRIPTION_CLASSES);

export function isRegulatoryClass(value: unknown): value is RegulatoryClass {
  return typeof value === 'string' && CONJUNTO_CLASSES.has(value);
}

/**
 * Todas as consultas de politica aceitam `string`, e nao `RegulatoryClass`, de proposito:
 * a classe chega do ERP e do banco, e um valor novo de enum tem de cair em **negacao**
 * (REG-011), nao em `undefined` silencioso.
 */
export function isControlledSubstance(regulatoryClass: string): boolean {
  return CONJUNTO_CONTROLADOS.has(regulatoryClass);
}

export function isMedication(regulatoryClass: string): boolean {
  return CONJUNTO_MEDICAMENTOS.has(regulatoryClass);
}

export function isPrescriptionClass(regulatoryClass: string): boolean {
  return CONJUNTO_TARJA_VERMELHA.has(regulatoryClass);
}

/** Converte com validacao; use no limite do sistema (importacao do ERP, parsing de payload). */
export function assertRegulatoryClass(value: unknown): RegulatoryClass {
  if (!isRegulatoryClass(value)) {
    throw new TypeError(
      `Classe regulatoria desconhecida: ${JSON.stringify(value)} (CONTRACTS §2.6 fixa 16 valores).`,
    );
  }
  return value;
}
